import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal, Tooltip, notification } from "antd";
import { CloseOutlined, HistoryOutlined } from "@ant-design/icons";
import DataTable from "../DeletedItemsComponents/DataTable/DataTable";
import DeletedActionButton from "../DeletedItemsComponents/DeletedActionButton/DeletedActionButton";
import { EntityTypeEnum, FetchStatusEnum } from "../../utils/enums";
import { archiveApi, describeArchiveFailure } from "./archiveApi";

// The Deleted items list's look, with only a restore.
import "../DeletedItemsComponents/DeletedItemsModal/DeletedItemsModal.scss";

const PAGE_SIZE = 10;

// What differs between the two kinds of archived item.
const KINDS = {
   [EntityTypeEnum.Workspace]: {
      label: "workspace",
      columns: [
         { title: "Name", dataIndex: "name", key: "name" },
         { title: "Description", dataIndex: "description", key: "description" }
      ],
      read: () => archiveApi.archivedWorkspaces().then((items) => ({ items, totalSize: items.length })),
      restore: (id) => archiveApi.restoreWorkspace(id)
   },
   [EntityTypeEnum.Project]: {
      label: "project",
      columns: [
         { title: "Project Name", dataIndex: "name", key: "name" },
         {
            title: "Manager",
            dataIndex: "manager",
            key: "manager",
            render: (manager) => manager?.fullName ?? ""
         }
      ],
      read: (workspaceId, page) =>
         archiveApi
            .archivedProjects(workspaceId, { page, pageSize: PAGE_SIZE })
            .then(({ projects, totalSize }) => ({ items: projects, totalSize })),
      restore: (id) => archiveApi.restoreProject(id)
   }
};

/**
 * The archived workspaces of the organization, or the archived projects of a
 * workspace (`workspaceId`), each with "Restore". A restore the plan has no
 * room for is refused, and the server's words say why. `onRestored` reloads
 * the list behind.
 */
function ArchivedItemsModal({ entityType, workspaceId, isModalOpen, onClose, onRestored }) {
   const kind = KINDS[entityType];
   const [data, setData] = useState([]);
   const [status, setStatus] = useState(FetchStatusEnum.Idle);
   const [pagination, setPagination] = useState({ page: 1, totalSize: 0 });
   // The item being restored: the others' buttons wait, and it is asked once.
   // The ref holds it from the first click on; the state only redraws.
   const [restoringId, setRestoringId] = useState(null);
   const restoringRef = useRef(null);

   const fetchData = useCallback(() => {
      setStatus(FetchStatusEnum.Loading);
      kind
         .read(workspaceId, pagination.page)
         .then(({ items, totalSize }) => {
            // A page emptied by a restore: back to the last page there is.
            const lastPage = Math.max(1, Math.ceil(totalSize / PAGE_SIZE));
            if (pagination.page > lastPage) {
               setPagination((previous) => ({ ...previous, page: lastPage, totalSize }));
               return;
            }
            setData(items);
            setPagination((previous) => ({ ...previous, totalSize }));
            setStatus(FetchStatusEnum.Success);
         })
         .catch(() => setStatus(FetchStatusEnum.Error));
   }, [kind, workspaceId, pagination.page]);

   useEffect(() => {
      if (isModalOpen) fetchData();
   }, [isModalOpen, fetchData]);

   const restore = async (item) => {
      if (restoringRef.current) return;
      restoringRef.current = item.id;
      setRestoringId(item.id);
      try {
         await kind.restore(item.id);
         notification.success({
            message: `${kind.label === "workspace" ? "Workspace" : "Project"} restored successfully`,
            description: `${item.name} is active again.`
         });
         fetchData();
         onRestored?.();
      } catch (failure) {
         notification.error({
            message: "Oops! A problem has occurred!",
            description: describeArchiveFailure(
               failure,
               `We had a problem restoring the ${kind.label}, please try again.`
            )
         });
      } finally {
         restoringRef.current = null;
         setRestoringId(null);
      }
   };

   const columns = [
      ...kind.columns,
      {
         key: "actions",
         fixed: "right",
         width: 80,
         render: (_, item) => (
            <Tooltip title="Restore">
               <span>
                  <DeletedActionButton
                     onClick={() => restore(item)}
                     disabled={Boolean(restoringId) && restoringId !== item.id}
                  >
                     <HistoryOutlined style={{ fontSize: 20, color: "#252938" }} />
                  </DeletedActionButton>
               </span>
            </Tooltip>
         )
      }
   ];

   return (
      <Modal
         prefixCls="deleted-items-modal"
         width={1046}
         closable
         open={isModalOpen}
         onCancel={onClose}
         closeIcon={<CloseOutlined style={{ color: "#5770CB", fontSize: "20px" }} />}
         footer={[
            <Button key="close" onClick={onClose} className="cancel-button">
               Close
            </Button>
         ]}
      >
         <header className="deleted-items-modal--title">
            <h3>Archived {kind.label}s</h3>
            <div>
               Archived {kind.label}s are hidden from everyone and don't count toward your plan.
               Nothing in them is deleted. Restore one to use it again, when your plan has room for
               it.
            </div>
         </header>
         <main className="deleted-items-modal--content">
            <DataTable
               dataSource={data}
               columns={columns}
               status={status}
               refetch={fetchData}
               entityType={entityType}
               pagination={{
                  total: pagination.totalSize,
                  pageSize: PAGE_SIZE,
                  current: pagination.page,
                  onChange: (page) => setPagination((previous) => ({ ...previous, page }))
               }}
            />
         </main>
      </Modal>
   );
}

export default ArchivedItemsModal;
